/**
 * Audio Upload Utilities
 * Handles uploading audio recordings to R2 storage via backend API
 */

import { apiUrl } from '../config';

export interface UploadRecordingParams {
  file: Blob;
  title?: string;
  durationSeconds: number;
  waveformData?: number[];
}

export interface UploadRecordingResponse {
  recording_id: string;
  file_url: string;
  title: string;
  duration_seconds: number;
  created_at: string;
}

/**
 * Upload an audio recording to R2 storage
 * @param params - Upload parameters including file blob and metadata
 * @returns Promise with upload response containing R2 URL
 */
export async function uploadRecording(
  params: UploadRecordingParams
): Promise<UploadRecordingResponse> {
  const { file, title, durationSeconds, waveformData } = params;

  // Create FormData for multipart upload
  const formData = new FormData();
  formData.append('file', file, 'recording.webm');
  formData.append('duration_seconds', durationSeconds.toString());
  
  if (title) {
    formData.append('title', title);
  }
  
  if (waveformData) {
    formData.append('waveform_data', JSON.stringify(waveformData));
  }

  // Get auth token from localStorage
  const tokens = JSON.parse(localStorage.getItem('music-legend-tokens') || '{}');
  const accessToken = tokens.access_token;

  if (!accessToken) {
    throw new Error('Authentication required. Please log in.');
  }

  // Upload to backend API
  const response = await fetch(apiUrl('/api/mixer/upload'), {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error?.message || `Upload failed with status ${response.status}`
    );
  }

  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Upload failed');
  }

  return result.data;
}

/**
 * Generate a signed URL for accessing a recording from R2
 * Note: Currently the backend returns direct URLs, but this function
 * is here for future signed URL implementation
 * @param fileUrl - The file URL from the upload response
 * @returns The URL to access the recording
 */
export function getRecordingUrl(fileUrl: string): string {
  // For now, construct the full CDN URL
  // In the future, this could request a signed URL from the backend
  const cdnBase = import.meta.env.VITE_CDN_BASE_URL || 'https://cdn.getmusiclegend.com';
  
  // Remove leading slash if present
  const cleanPath = fileUrl.startsWith('/') ? fileUrl.substring(1) : fileUrl;
  
  return `${cdnBase}/${cleanPath}`;
}
